import React, { createContext, useContext, useState, useEffect } from "react";
import { getRolesPermissionsByUserId } from "../services/auth";
import { category } from "../data/category";
import { mobileCategory } from "../data/mobile_category";
import { jwtDecode } from "jwt-decode";
import CryptoJS from "crypto-js";
import { oidcConfig } from "../config/config";

// Create context
const PermissionContext = createContext(undefined);

// Utility function to check if user has any permission for an item
const hasItemPermission = (roles, categoryKey, categoryKey2) => {
  const isSuperAdmin = roles.some((role) => role.roleName === "Super Admin");
  if (isSuperAdmin) return true;

  return roles.some(
    (role) =>
      Array.isArray(role?.permissions) &&
      (role.permissions.some((perm) => perm.startsWith(categoryKey)) ||
        (categoryKey2 &&
          role.permissions.some((perm) => perm === categoryKey2)))
  );
};

// Utility function to filter categories based on permissions
const filterCategoriesByPermissions = (categories, roles) => {
  return categories
    .map((cat) => ({
      ...cat,
      subcategories: cat.subcategories
        .map((subcat) => ({
          ...subcat,
          items: subcat.items.filter((item) =>
            hasItemPermission(roles, item.categoryKey, item.categoryKey2)
          ),
        }))
        .filter((subcat) => subcat.items.length > 0),
    }))
    .filter((cat) => cat.subcategories.length > 0);
};

export const PermissionProvider = ({ children }) => {
  const [roles, setRoles] = useState([]);
  const [filteredWebCategories, setFilteredWebCategories] = useState([]);
  const [filteredMobileCategories, setFilteredMobileCategories] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchPermissions = async (userId) => {
    setIsLoaded(false);
    setError(null);

    try {
      let data;
      // Check sessionStorage and JWT expiration
      const cachedPermissions = sessionStorage.getItem("globalPermissions");
      const token = localStorage.getItem("access_token");
      let isTokenValid = false;

      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
          isTokenValid = decodedToken.exp > currentTime; // Check if token is still valid
        } catch (err) {
          console.error("Failed to decode token for expiration check:", err);
        }
      }

      if (cachedPermissions && isTokenValid) {
        try {
          // Decrypt cached permissions
          const bytes = CryptoJS.AES.decrypt(
            cachedPermissions,
            oidcConfig.secretCrypt
          );
          const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
          data = decryptedData;
          // console.log("API Persisted from sessionStorage (decrypted):", data);
        } catch (err) {
          console.error("Failed to decrypt cached permissions:", err);
          // Clear invalid cached data to avoid repeated errors
          sessionStorage.removeItem("globalPermissions");
        }
      }

      const fetchedFresh = !data;
      if (fetchedFresh) {
        // Fetch permissions from the API; only cache them once we know
        // the shape is valid, so a malformed/error response never gets
        // persisted as if it were a good cache entry.
        data = await getRolesPermissionsByUserId(userId);
      }

      // Validate data is an array
      if (!Array.isArray(data)) {
        throw new Error("Invalid data format: Expected an array of roles");
      }

      if (fetchedFresh) {
        const encrypted = CryptoJS.AES.encrypt(
          JSON.stringify(data),
          oidcConfig.secretCrypt
        ).toString();
        sessionStorage.setItem("globalPermissions", encrypted);
      }

      // Validate each role has a permissions array
      const validRoles = data.filter(
        (role) =>
          role &&
          typeof role === "object" &&
          Array.isArray(role.permissions) &&
          typeof role.roleId === "string" &&
          typeof role.roleName === "string"
      );
      setRoles(validRoles);
      // Filter web and mobile categories based on permissions
      const filteredWeb = filterCategoriesByPermissions(category, validRoles);
      const filteredMobile = filterCategoriesByPermissions(
        mobileCategory,
        validRoles
      );
      setFilteredWebCategories(filteredWeb);
      setFilteredMobileCategories(filteredMobile);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setError(error.message || "Unknown error occurred");
    } finally {
      // Always resolve the loading state, even on failure — otherwise
      // isLoaded gets stuck false forever and consumers (e.g.
      // VideoListsPage's effect) never re-run to show the error or retry.
      setIsLoaded(true);
    }
  };
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const userId = decodedToken["sub"];
        if (userId) {
          fetchPermissions(userId);
        }
      } catch (err) {
        console.error("Failed to decode token:", err);
        setError("Invalid token");
        setIsLoaded(true);
      }
    } else {
      setIsLoaded(true); // No token, allow UI to proceed
    }
  }, []);

  const hasPermission = (permission) => {
    // Check for Super Admin role to grant full access
    const isSuperAdmin = roles.some((role) => role.roleName === "Super Admin");
    if (isSuperAdmin) return true;

    return roles.some(
      (role) =>
        Array.isArray(role?.permissions) &&
        role.permissions.includes(permission)
    );
  };

  return (
    <PermissionContext.Provider
      value={{
        roles,
        hasPermission,
        isLoaded,
        error,
        filteredWebCategories,
        filteredMobileCategories,
        fetchPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};
